import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { CancelAdvanceBookingAssignLockers } from "../api/api";
import { BUTTON_PRIMARY, BUTTON_WARNING } from "../assets/constants/Constants";
import { getPathForNextPage, getReservationStartDate, isCancellationAllowed, isModificationAllowed } from "../lib/Utils";
import CustomDialog from "./CustomDialog";
import ButtonComponent from "./ButtonComponent";
import { useTranslation, Trans } from "react-i18next";
import { MAP, USER_ACCOUNT } from "../assets/constants/PageList";

export default function UpcomingLockersComponent({ locker }) {
  const history = useHistory();
  const { t } = useTranslation();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isBlockedDialogOpen, setIsBlockedDialogOpen] = useState(false);
  const [isModifyDialogOpen, setIsModifyDialogOpen] = useState(false);
  const [selectedLocker, setSelectedLocker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [blockedDialogTitle, setBlockedDialogTitle] = useState(t("cancellation_not_allowed_title"));
  const [blockedDialogMessage, setBlockedDialogMessage] = useState(
    t("cancellation_not_allowed_within_hours_text", { hours: 3 })
  );

  const HandleLockerCancel = (lockerItem) => {
    setSelectedLocker(lockerItem);
    setIsCancelDialogOpen(true);
  };

  const HandleLockerModication = (lockerItem) => {
    setSelectedLocker(lockerItem);
    setIsModifyDialogOpen(true);
  };

  const confirmModifyBooking = async () => {
    try {
      const reserveStart = getReservationStartDate(selectedLocker);
      console.log("Reservation Start:", reserveStart , "with selected locker is:", selectedLocker);
      if (!reserveStart) return;
      

      if (!isModificationAllowed(reserveStart, 3)) {
        setIsModifyDialogOpen(false);
        setBlockedDialogTitle(t("modification_not_allowed_title"));
        setBlockedDialogMessage(t("modification_not_allowed_within_hours_text", { hours: 3 }));
        setIsBlockedDialogOpen(true);
        return;
      }

      setLoading(true);
      const requestdata = {
        transaction_id: selectedLocker.ref_id,
        cancellation_reason: "Cancelled by user for modification",
      };

      await CancelAdvanceBookingAssignLockers(requestdata);
      setIsModifyDialogOpen(false);
      history.push(getPathForNextPage(USER_ACCOUNT, MAP));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const confirmCancelBooking = async () => {
    try {
      const reserveStart = getReservationStartDate(selectedLocker);
      if (!reserveStart) return;

      if (!isCancellationAllowed(reserveStart, 3)) {
        setIsCancelDialogOpen(false);
        setBlockedDialogTitle(t("cancellation_not_allowed_title"));
        setBlockedDialogMessage(t("cancellation_not_allowed_within_hours_text", { hours: 3 }));
        setIsBlockedDialogOpen(true);
        return;
      }
      setLoading(true);

      const requestdata = {
        transaction_id: selectedLocker.ref_id,
        cancellation_reason: "Cancelled by user",
      };

      await CancelAdvanceBookingAssignLockers(requestdata);

      setIsCancelDialogOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const CancelBookingContent = ({ locker }) => (
    <div>
      <h3>{t("cancel_booking_title")}</h3>
      <p>
        <Trans
          i18nKey="cancel_booking_confirm_text"
          values={{ lockerNumber: locker?.lockerNumber }}
          components={{ strong: <strong /> }}
        />
      </p>
    </div>
  );

  const ModifyBookingContent = ({ locker }) => (
    <div>
      <h3>{t("modify_booking_title")}</h3>
      <p>
        <Trans
          i18nKey="modify_booking_description_text"
          values={{ lockerNumber: locker?.lockerNumber }}
          components={{ strong: <strong /> }}
        />
      </p>
    </div>
  );

  const BlockedContent = () => (
    <div>
      <h3>{blockedDialogTitle}</h3>
      <p>{blockedDialogMessage}</p>
    </div>
  );

  return (
    <>
      <div className="locker-card upcoming">
        <div className="top">
          <div className="title">{locker.name}</div>
          <div className={`status ${locker.status}`}>
            {locker.status}
          </div>
        </div>

        <div className="sub-text">
          {t("locker_number_and_size_text", {
            lockerNumber: locker.lockerNumber,
            size: locker.size,
          })}
        </div>

        <div className="date-section">
          <div className="date-box">
              <span className="label">{t("check_in_label")}</span>
              <span className="value">{locker.checkInDate}</span>
              <span className="value">{locker.checkInTime}</span>
          </div>

          <div className="date-box">
            <span className="label">{t("check_out_label")}</span>
            <span className="value">{locker.checkOutDate}</span>
            <span className="value">{locker.checkOutTime}</span>
          </div>
        </div>

        <hr />

        <div className="button-container">
          <ButtonComponent
            type={BUTTON_PRIMARY}
            handleClick={() => HandleLockerModication(locker)}
            title="modify_booking_button"
            disabled={loading}
          />
          <ButtonComponent
            type={BUTTON_WARNING}
            handleClick={() => HandleLockerCancel(locker)}
            title="cancel_booking_button"
            disabled={loading}
          />
        </div>
      </div>

      <CustomDialog
        dialogVisible={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        handleCancel={() => setIsCancelDialogOpen(false)}
        handleAccept={confirmCancelBooking}
        buttonOneTitle="noText"
        buttonTwoTitle={loading ? "cancelling_text" : "yes_cancel_text"}
        buttonProps={{
          buttonOne: { type: BUTTON_WARNING },
          buttonTwo: { type: BUTTON_PRIMARY, disabled: loading },
        }}
      >
        {() => <CancelBookingContent locker={selectedLocker} />}
      </CustomDialog>

      <CustomDialog
        dialogVisible={isModifyDialogOpen}
        onClose={() => setIsModifyDialogOpen(false)}
        handleCancel={() => setIsModifyDialogOpen(false)}
        handleAccept={confirmModifyBooking}
        buttonOneTitle="noText"
        buttonTwoTitle={loading ? "pleaseWaitText" : "yes_modify_text"}
        buttonProps={{
          buttonOne: { type: BUTTON_WARNING },
          buttonTwo: { type: BUTTON_PRIMARY, disabled: loading },
        }}
      >
        {() => <ModifyBookingContent locker={selectedLocker} />}
      </CustomDialog>

      <CustomDialog
        dialogVisible={isBlockedDialogOpen}
        onClose={() => setIsBlockedDialogOpen(false)}
        handleCancel={() => setIsBlockedDialogOpen(false)}
        buttonOneTitle="button_ok"
        buttonProps={{
          buttonOne: { type: BUTTON_PRIMARY },
        }}
      >
        {() => <BlockedContent />}
      </CustomDialog>
    </>
  );
}
